<script setup lang="ts">
import { ModelThrowComponentsTab, Tab, Tabs } from 'tir-components';
import { computed, onMounted, ref, watch } from 'vue';
import { RouterView, useRouter } from 'vue-router';
import { MonitoringHeader } from './components';
import { MonitoringHeaderFilterModel } from './models';
import GetEmployeesWithAccessToProjectItemModel from './models/GetEmployeesWithAccessToProjectItemModel';
import { MonitoringPagesEnum } from './enums';
import { MonitoringsAdapterService } from './adapter-service';
import GetEmployeesWithAccessToProjectRequestModel from './models/GetEmployeesWithAccessToProjectRequestModel';
import { debounce } from 'tir-pm-debounce';

/**Роутер */
const router = useRouter();
/**адаптер */
const adapter = new MonitoringsAdapterService();

/**Первое число текущего месяца с годом */
const firstDayOfMonth = new Date(new Date().getFullYear(), new Date().getMonth(), 1);

/**Текущая дата, месяц и год */
const currentDayOfMonth = new Date();
/**Поиск юзеров */
const searchEmployeesProject = debounce((search: string) => getEmployeesProject(search), 300);
/**Вкладки */
const tabs = ['По дням', 'По тикетам'];
/**Текущая вкладка */
const currentTab = ref<string>();
/**Состояние чекбокса Все мои проекты из локалсторедж */
const stateAllMyProjectsChecked = localStorage.getItem('stateAllMyProjectsChecked');

/**фильтр  */
const filter = ref(
  new MonitoringHeaderFilterModel({
    Search: '',
    SearchPerformers: '',
    SelectedPerformer: {} as GetEmployeesWithAccessToProjectItemModel,
    Performers: [],
    AllMyProjectsChecked: JSON.parse(stateAllMyProjectsChecked) || false,
    TotalWorkTime: 0,
    DateStart: new Date(firstDayOfMonth),
    DateEnd: new Date(currentDayOfMonth),
  }),
);

/**Обновленная начальная дата */
const updateDateStart = computed(() => {
  if (!filter.value.DateStart) return null;

  const startDate = new Date(filter.value.DateStart);
  startDate.setUTCDate(startDate.getUTCDate() + 1);
  startDate.setUTCHours(0, 0, 0, 0);

  return startDate;
});
/**Обновленная конечная дата */
const updateDateEnd = computed(() => {
  if (!filter.value.DateEnd) return null;

  const endDate = new Date(filter.value.DateEnd);
  endDate.setUTCDate(endDate.getUTCDate() + 1);
  endDate.setUTCHours(0, 0, 0, 0);

  return endDate;
});

/**Отслеживание изменения пути */
watch(
  () => router.currentRoute.value.name,
  () => {
    const routerName = router.currentRoute.value.name;
    filter.value.Search = '';
    if (routerName === MonitoringPagesEnum.Days) {
      currentTab.value = String(tabs[0]);
    } else if (routerName === MonitoringPagesEnum.Tickets) {
      currentTab.value = String(tabs[1]);
    }
  },
  { immediate: true },
);

/**После рендера компонента */
onMounted(async () => {
  await getEmployeesProject();
});

/**Выбрать исполнителя */
const selectPerformer = async (performer: GetEmployeesWithAccessToProjectItemModel) => {
  await getEmployeesProject();

  filter.value.Performers = filter.value.Performers.filter(
    (item) => item.UserId !== performer.UserId,
  );
  filter.value.SelectedPerformer = performer;
};
/**Получить исполнителей проекта*/
const getEmployeesProject = async (_search?: string) => {
  const projectId = router.currentRoute.value.params.projectId;
  const response = await adapter.getEmployeesWithAccessToProject(
    new GetEmployeesWithAccessToProjectRequestModel({
      ProjectId: +projectId,
      Search: _search,
    }),
  );
  filter.value.Performers = response.Value.Items;
};
/**Очистить фильтр */
const clearFilter = async () => {
  filter.value.SelectedPerformer = {} as GetEmployeesWithAccessToProjectItemModel;
  await getEmployeesProject();
};
</script>

<template>
  <div class="monitorings__block">
    <div class="monitorings__header">
      <MonitoringHeader
        :filter="filter"
        v-model:selectedDateStart="filter.DateStart"
        v-model:selectedDateEnd="filter.DateEnd"
        v-model:searchPerformers="filter.SearchPerformers"
        v-model:selectPerformer="filter.SelectedPerformer"
        :search="filter.Search"
        :performers="filter.Performers"
        :dateStart="filter.DateStart"
        :dateEnd="filter.DateEnd"
        :totalWorkTime="filter.TotalWorkTime"
        @update:search="filter.Search = $event"
        @selectPerformer="selectPerformer"
        @update:searchPerformers="searchEmployeesProject"
        @clearFilter="clearFilter"
      />
    </div>
    <RouterView
      :filter="filter"
      :selectDateStart="updateDateStart"
      :selectDateEnd="updateDateEnd"
    />
  </div>
</template>

<style scoped lang="scss">
.monitorings {
  &__block {
    height: calc(100vh - 9.875rem);
    padding: 1.5rem;
    background-color: var(--bg-base, #fff);
    border-radius: 0.5rem;
    box-shadow: 0rem 0.25rem 0.5rem 0rem rgba(0, 0, 0, 0.04),
      0rem 0rem 0.063rem 0rem rgba(0, 0, 0, 0.04);
  }
  &__tabs {
    white-space: nowrap;
    width: max-content;
  }
  &__header {
    display: flex;
    align-items: center;
    gap: 1.5rem;
  }
}
</style>
